import {
  getDisplayPhoneNumber,
  getPhoneData,
  type ContactLanguage,
  type DisplayContact,
  type ParsedContact,
} from '@aikoa/common'
import { type Language, useTranslations } from '@aikoa/i18n/react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { z } from 'zod/v4'
import { ContactRow as ContactListItem } from '~/components/contacts/ContactRow'
import Icon from '~/components/Icon'
import { Button, ButtonLabel } from '~/components/ui/button'
import FloatingPopover from '~/components/ui/floating-popover'
import { Input } from '~/components/ui/input'
import { LoadingSkeleton } from '~/components/ui/skeleton'
import { Text } from '~/components/ui/text'
import { useAddContact, useContactsByIds, useContactsPaginated } from '~/data/contacts'
import { cn } from '~/lib/utils'
import { LanguageInput } from '../LanguageInput'

const DEFAULT_MAX_CONTACTS = 10
const DEFAULT_LANGUAGE: ContactLanguage = 'fi'
const contactEmailSchema = z.email()

type EditingRow = {
  id: string
  kind: 'editing'
  nameValue: string
  phoneDisplayValue: string
  phoneStoredValue: string
  phoneIsValid: boolean
  emailValue: string
  emailVisible: boolean
  languageValue: ContactLanguage
  errorMessage: string | null
  isResolving: boolean
}

type ResolvedRow = {
  id: string
  kind: 'resolved'
  contactId: string
  name: string
  phone: string | null
  email: string | null
  contact: ParsedContact
}

type MultiRowContactSelectProps = {
  selectedContactIds: string[]
  onChangeSelectedContactIds: (ids: string[]) => void
  isContactSelectable?: (contact: DisplayContact) => boolean
  getContactIneligibilityMessage?: (contact: DisplayContact) => string | null
  onHasUnsavedRowsChange?: (hasUnsavedRows: boolean) => void
  maxContacts?: number
  limitReachedMessage?: ReactNode
  className?: string
}

function createEditableRow(): EditingRow {
  return {
    id: 'pending',
    kind: 'editing',
    nameValue: '',
    phoneDisplayValue: '',
    phoneStoredValue: '',
    phoneIsValid: false,
    emailValue: '',
    emailVisible: false,
    languageValue: DEFAULT_LANGUAGE,
    errorMessage: null,
    isResolving: false,
  }
}

export function MultiRowContactSelect({
  selectedContactIds,
  onChangeSelectedContactIds,
  isContactSelectable,
  getContactIneligibilityMessage,
  onHasUnsavedRowsChange,
  maxContacts = DEFAULT_MAX_CONTACTS,
  limitReachedMessage,
  className,
}: MultiRowContactSelectProps) {
  const { t } = useTranslations('feedback')

  const addContactMutation = useAddContact()
  const [editableRow, setEditableRow] = useState<EditingRow | null>(createEditableRow())

  const { data: selectedContactsByIds, isLoading: isLoadingSelectedContactsByIds } =
    useContactsByIds(selectedContactIds)

  const selectedRows = useMemo(() => {
    if (!selectedContactsByIds || selectedContactsByIds.length === 0) {
      return []
    }

    return selectedContactIds
      .map((contactId) => {
        const contact = selectedContactsByIds.find((contact) => contact.id === contactId)

        if (!contact) {
          return null
        }

        return {
          id: contact.id,
          kind: 'resolved' as const,
          contactId: contact.id,
          name: contact.name,
          phone: contact.phone,
          email: contact.email ?? null,
          contact,
        } satisfies ResolvedRow
      })
      .filter((contact): contact is ResolvedRow => !!contact)
  }, [selectedContactIds, selectedContactsByIds])

  const hasEditingRowWithContent = useMemo(
    () =>
      editableRow &&
      (editableRow.nameValue.trim().length > 0 ||
        editableRow.phoneDisplayValue.trim().length > 0 ||
        editableRow.emailValue.trim().length > 0),
    [editableRow]
  )

  const isMaxReached = selectedRows.length >= maxContacts

  const searchTerm = useMemo(() => {
    if (!editableRow) {
      return ''
    }

    return editableRow.nameValue.trim() || editableRow.emailValue.trim()
  }, [editableRow])

  const {
    contacts: suggestedContacts,
    isLoading: isLoadingSuggestedContacts,
    hasNextPage: hasNextContactsPage,
    isFetchingNextPage: isFetchingNextContactsPage,
    fetchNextPage: fetchNextContactsPage,
    isSearching: isSearchingSuggestedContacts,
  } = useContactsPaginated({
    search: searchTerm,
  })

  const suggestions = useMemo(() => {
    if (!editableRow) {
      return []
    }

    return suggestedContacts
  }, [editableRow, suggestedContacts])

  const isContactSelectableValue = useCallback(
    (contact: DisplayContact) => {
      if (!isContactSelectable) {
        return true
      }

      return isContactSelectable(contact)
    },
    [isContactSelectable]
  )

  const getContactIneligibilityMessageValue = useCallback(
    (contact: DisplayContact) => {
      if (!getContactIneligibilityMessage) {
        return null
      }

      return getContactIneligibilityMessage(contact)
    },
    [getContactIneligibilityMessage]
  )

  useEffect(() => {
    onHasUnsavedRowsChange?.(
      !!editableRow &&
        (editableRow.nameValue.trim().length > 0 ||
          editableRow.phoneDisplayValue.trim().length > 0 ||
          editableRow.emailValue.trim().length > 0)
    )
  }, [hasEditingRowWithContent, onHasUnsavedRowsChange, editableRow])

  const saveContactValues = useCallback(async () => {
    const trimmedName = editableRow?.nameValue.trim()
    const trimmedEmail = editableRow?.emailValue.trim() || undefined
    const normalizedPhone = editableRow?.phoneDisplayValue.trim()
      ? (getPhoneData(editableRow.phoneDisplayValue, 'FI')?.phoneNumber ?? undefined)
      : undefined
    const hasValidEmail = !trimmedEmail || contactEmailSchema.safeParse(trimmedEmail).success

    if (!trimmedName || (!normalizedPhone && !trimmedEmail) || !hasValidEmail) {
      return false
    }

    try {
      const createdContact = await addContactMutation.mutateAsync({
        name: trimmedName,
        phone: normalizedPhone,
        email: trimmedEmail,
        consent_confirmed: true,
        language: editableRow?.languageValue ?? DEFAULT_LANGUAGE,
      })

      if (!createdContact?.id) {
        throw new Error('CONTACT_CREATE_FAILED')
      }

      if (selectedContactIds.includes(createdContact.id)) {
        setEditableRow((currentEditableRow) => {
          if (!currentEditableRow) {
            return currentEditableRow
          }

          return {
            ...currentEditableRow,
            errorMessage: t(($) => $.requestFlow.multiContactAlreadyAdded),
          }
        })
        return false
      }

      if (!isContactSelectableValue(createdContact)) {
        const contactIneligibilityMessage = getContactIneligibilityMessageValue(createdContact)

        setEditableRow((currentEditableRow) => {
          if (!currentEditableRow) {
            return currentEditableRow
          }

          return {
            ...currentEditableRow,
            errorMessage:
              contactIneligibilityMessage ?? t(($) => $.errors.selectOtherContactOrAsset),
          }
        })
        return false
      }

      onChangeSelectedContactIds([...selectedContactIds, createdContact.id])
      setEditableRow(createEditableRow())
      return true
    } catch (error) {
      console.error('Failed to add contact', error)
      return false
    }
  }, [
    addContactMutation,
    editableRow,
    isContactSelectableValue,
    getContactIneligibilityMessageValue,
    selectedContactIds,
    onChangeSelectedContactIds,
    t,
  ])

  const handleAddNewRow = useCallback(async () => {
    if (editableRow) {
      const saved = await saveContactValues()

      if (!saved) {
        return
      }
    }

    setEditableRow(createEditableRow())
  }, [editableRow, saveContactValues])

  const handleRemoveRow = useCallback(
    (rowId: string) => {
      onChangeSelectedContactIds(selectedContactIds.filter((id) => id !== rowId))
    },
    [selectedContactIds, onChangeSelectedContactIds]
  )

  const handleSuggestionsScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (isSearchingSuggestedContacts || !hasNextContactsPage || isFetchingNextContactsPage) {
        return
      }

      const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent
      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height)

      if (distanceFromBottom <= 60) {
        fetchNextContactsPage()
      }
    },
    [
      fetchNextContactsPage,
      hasNextContactsPage,
      isFetchingNextContactsPage,
      isSearchingSuggestedContacts,
    ]
  )

  const isEditableRowValid = useMemo(() => {
    const trimmedName = editableRow?.nameValue.trim() ?? ''
    const trimmedPhone = editableRow?.phoneDisplayValue.trim() ?? ''
    const trimmedEmail = editableRow?.emailValue.trim() ?? ''
    const hasValidPhone =
      !trimmedPhone || (!!editableRow?.phoneStoredValue && editableRow.phoneIsValid)
    const hasValidEmail = !trimmedEmail || contactEmailSchema.safeParse(trimmedEmail).success
    const hasAtLeastOneChannel =
      (!!editableRow?.phoneStoredValue && editableRow.phoneIsValid) ||
      contactEmailSchema.safeParse(trimmedEmail).success

    return !!trimmedName && hasAtLeastOneChannel && hasValidPhone && hasValidEmail
  }, [
    editableRow?.emailValue,
    editableRow?.nameValue,
    editableRow?.phoneDisplayValue,
    editableRow?.phoneIsValid,
    editableRow?.phoneStoredValue,
  ])

  const didFocusInputRef = useRef(false)
  const [inputIsFocused, setInputIsFocused] = useState(false)

  const showSuggestionsLoadingState =
    !!editableRow && (isLoadingSuggestedContacts || isFetchingNextContactsPage)
  const isDropdownOpen =
    inputIsFocused && !!editableRow && (suggestions.length > 0 || showSuggestionsLoadingState)
  const rowHasInput =
    !!editableRow &&
    (editableRow.nameValue.trim().length > 0 ||
      editableRow.phoneDisplayValue.trim().length > 0 ||
      editableRow.emailValue.trim().length > 0)

  const canAddRow =
    !isMaxReached && ((hasEditingRowWithContent && isEditableRowValid) || !editableRow)

  const onFocusInput = useCallback(() => {
    setTimeout(() => {
      didFocusInputRef.current = true
    }, 10)
    setInputIsFocused(true)
  }, [])

  const onBlurInput = useCallback(() => {
    didFocusInputRef.current = false
    setTimeout(() => {
      if (!didFocusInputRef.current) {
        setInputIsFocused(false)
      }
    }, 200)
  }, [])

  const showSelectedRowsSkeleton = isLoadingSelectedContactsByIds && selectedRows.length === 0

  return (
    <View className={cn('gap-3', className)}>
      {showSelectedRowsSkeleton ? <LoadingSkeleton isLoading={true} className="h-10" /> : null}
      {selectedRows.map((row) => {
        const contactIneligibilityMessage = getContactIneligibilityMessageValue(row.contact)

        return (
          <ContactListItem
            key={row.id}
            contact={row.contact}
            className="flex-row items-center p-3">
            {contactIneligibilityMessage && (
              <Text className="text-sm/normal text-destructive">{contactIneligibilityMessage}</Text>
            )}
            <View className="flex-row justify-end">
              <Pressable
                testID={`feedback-contact-remove-${row.id}`}
                onPress={() => handleRemoveRow(row.id)}
                accessibilityLabel={t('requestFlow.multiContactRemove')}
                hitSlop={8}
                className="-mr-1 size-8 items-center justify-center rounded-full hover:bg-muted/80 active:bg-muted">
                <Icon name="trash-outline" size={14} className="text-destructive" />
              </Pressable>
            </View>
          </ContactListItem>
        )
      })}

      {!isMaxReached && (
        <>
          <Animated.View
            key="editable-row"
            className="mt-2"
            entering={FadeIn.duration(150)}
            exiting={FadeOut.duration(100)}>
            <FloatingPopover
              isOpen={isDropdownOpen}
              offset={6}
              contentClassName="max-h-[260px] bg-background"
              trigger={
                <Animated.View
                  className={cn(
                    'relative gap-3 transition-all duration-100 ease-out',
                    !!editableRow?.errorMessage && 'pb-6 duration-150'
                  )}>
                  <Text className="text-pretty font-light text-xs/normal text-tertiary">
                    {t('requestFlow.contactFormInstructions')}
                  </Text>

                  <View className="gap-2">
                    <View className="flex-row items-start gap-2">
                      <Input
                        testID="feedback-contact-name-input"
                        value={editableRow?.nameValue}
                        placeholder={t('requestFlow.multiContactNamePlaceholder')}
                        onChangeText={(nameText) =>
                          setEditableRow({
                            ...(editableRow ?? createEditableRow()),
                            nameValue: nameText,
                            errorMessage: null,
                          })
                        }
                        onFocus={onFocusInput}
                        onBlur={onBlurInput}
                        maxLength={100}
                        className="shadow-none! rounded-lg border-tertiary py-1"
                        variant="box"
                        inputClassName="h-8 text-sm/normal"
                        editable={!editableRow?.isResolving}
                      />
                      <Input
                        testID="feedback-contact-phone-input"
                        type="phone"
                        value={editableRow?.phoneDisplayValue}
                        placeholder={t('requestFlow.multiContactPhonePlaceholder')}
                        onChangeText={(
                          displayPhoneNumber,
                          _countryCode,
                          storedPhoneNumber,
                          isValid
                        ) =>
                          setEditableRow({
                            ...(editableRow ?? createEditableRow()),
                            phoneDisplayValue: displayPhoneNumber,
                            phoneStoredValue: storedPhoneNumber,
                            phoneIsValid: isValid,
                            errorMessage: null,
                          })
                        }
                        onFocus={onFocusInput}
                        onBlur={onBlurInput}
                        className="shadow-none! rounded-lg border-tertiary py-1"
                        countrySelectClassName="-ml-1 rounded-md px-1"
                        variant="box"
                        inputClassName="h-8 text-sm/normal"
                        editable={!editableRow?.isResolving}
                      />
                    </View>
                    <View className="flex-row items-center gap-8">
                      {editableRow?.emailVisible ? (
                        <Input
                          testID="feedback-contact-email-input"
                          value={editableRow.emailValue}
                          placeholder={t('requestFlow.multiContactEmailPlaceholder')}
                          onChangeText={(emailText) =>
                            setEditableRow({
                              ...(editableRow ?? createEditableRow()),
                              emailValue: emailText,
                              errorMessage: null,
                            })
                          }
                          onFocus={onFocusInput}
                          onBlur={onBlurInput}
                          keyboardType="email-address"
                          autoCapitalize="none"
                          className="shadow-none! flex-1 rounded-lg border-tertiary py-1"
                          variant="box"
                          inputClassName="h-8 text-sm/normal"
                          editable={!editableRow?.isResolving}
                        />
                      ) : (
                        <Pressable
                          testID="feedback-contact-add-email"
                          onPress={() =>
                            setEditableRow({
                              ...(editableRow ?? createEditableRow()),
                              emailVisible: true,
                            })
                          }
                          className="mr-auto self-center px-1 py-0.5 active:opacity-70">
                          <Text className="text-xs/normal text-secondary underline underline-offset-2">
                            {t('requestFlow.multiContactAddEmail')}
                          </Text>
                        </Pressable>
                      )}
                      <View className="flex-1 items-end">
                        <LanguageInput
                          value={(editableRow?.languageValue ?? DEFAULT_LANGUAGE) as Language}
                          onChange={(locale) =>
                            setEditableRow({
                              ...(editableRow ?? createEditableRow()),
                              languageValue: locale as ContactLanguage,
                            })
                          }
                          showLabel
                          align="start"
                          triggerClassName="-mr-2 h-7 border-0 px-3 py-0"
                          iconClassName="text-tertiary"
                        />
                      </View>
                    </View>
                  </View>

                  {!!editableRow?.errorMessage && (
                    <Animated.Text
                      entering={FadeIn.duration(150)}
                      exiting={FadeOut.duration(100)}
                      className="absolute -bottom-0.5 left-0 text-xs/normal text-destructive">
                      {editableRow?.errorMessage}
                    </Animated.Text>
                  )}
                </Animated.View>
              }>
              <Animated.View entering={FadeIn.duration(150)} exiting={FadeOut.duration(100)}>
                <ScrollView
                  testID="feedback-contact-suggestions"
                  className="max-h-[130px]"
                  contentContainerClassName="gap-0.5 p-1.5"
                  onScroll={handleSuggestionsScroll}
                  scrollEventThrottle={16}
                  keyboardShouldPersistTaps="handled">
                  {suggestions.map((suggestionContact) => {
                    const isSuggestionSelectable = isContactSelectableValue(suggestionContact)

                    const contactIneligibilityMessage =
                      getContactIneligibilityMessageValue(suggestionContact)

                    return (
                      <Pressable
                        testID={`feedback-contact-suggestion-${suggestionContact.id}`}
                        key={suggestionContact.id}
                        disabled={!isSuggestionSelectable}
                        aria-disabled={!isSuggestionSelectable}
                        accessibilityState={{ disabled: !isSuggestionSelectable }}
                        onPress={() => {
                          if (!isSuggestionSelectable) {
                            return
                          }

                          didFocusInputRef.current = false
                          setInputIsFocused(false)

                          onChangeSelectedContactIds([...selectedContactIds, suggestionContact.id])
                          setEditableRow(createEditableRow())
                        }}
                        className={cn(
                          'rounded-md px-3 py-2',
                          isSuggestionSelectable
                            ? 'hover:bg-muted active:bg-muted/80'
                            : 'opacity-50'
                        )}>
                        <View className="flex-row items-center gap-3">
                          <View className="flex-1">
                            <Text className="text-sm/normal text-foreground">
                              {suggestionContact.name}
                            </Text>
                            {suggestionContact.email && (
                              <Text className="text-xs/normal text-secondary">
                                {suggestionContact.email}
                              </Text>
                            )}
                            {!isSuggestionSelectable && contactIneligibilityMessage && (
                              <Text className="text-xs/normal text-destructive">
                                {contactIneligibilityMessage}
                              </Text>
                            )}
                          </View>

                          <Text className="text-sm/normal text-secondary">
                            {suggestionContact.phone
                              ? getDisplayPhoneNumber(suggestionContact.phone)
                              : '–'}
                          </Text>
                        </View>
                      </Pressable>
                    )
                  })}
                  {showSuggestionsLoadingState && (
                    <View className="items-center justify-center py-3">
                      <ActivityIndicator size="small" />
                    </View>
                  )}
                </ScrollView>
              </Animated.View>
            </FloatingPopover>
          </Animated.View>
          {editableRow?.isResolving ? (
            <View className="mt-1 pl-1">
              <ActivityIndicator size="small" />
            </View>
          ) : rowHasInput ? (
            <Button
              testID="feedback-contact-save-button"
              variant="default"
              size="sm"
              loading={editableRow?.isResolving ?? addContactMutation.isPending}
              onPress={() => {
                void saveContactValues()
              }}
              disabled={!isEditableRowValid}
              accessibilityLabel={t('requestFlow.multiContactSave')}
              hitSlop={8}
              className="-mr-1 mt-3">
              <ButtonLabel icon={<Icon name="checkmark" size={16} />}>
                {t('requestFlow.multiContactSave')}
              </ButtonLabel>
            </Button>
          ) : null}
        </>
      )}

      {isMaxReached ? (
        limitReachedMessage ? (
          <Animated.View entering={FadeIn.duration(200)} className="pt-4">
            {limitReachedMessage}
          </Animated.View>
        ) : null
      ) : canAddRow ? (
        <Button
          testID="feedback-contact-add-row-button"
          onPress={() => {
            void handleAddNewRow()
          }}
          variant="ghost"
          size="sm"
          className="mt-3 pl-4"
          disabled={addContactMutation.isPending}>
          <ButtonLabel
            className="decoration-0 underline-offset-4"
            icon={<Icon name="add" size={16} className="text-secondary no-underline" />}>
            <Text className="font-medium text-sm/normal text-secondary underline underline-offset-2">
              {t('requestFlow.multiContactAddRow')}
            </Text>
          </ButtonLabel>
        </Button>
      ) : null}
    </View>
  )
}
